/**
 * `ToastHost`: draws v2's toast (`@rista/core/toast`), mounted ONCE by each app's shell. One body
 * for both apps, no platform split (the user, 2026-10-05), in web v1's react-toastify layout,
 * bottom-centre (`ReactToastify.css` 11.1). At 480 or narrower it spans the width, as react-toastify
 * does. Its colours are roles since DS1 (`look.ts`, QD8): the kind's status solid, not the dark theme.
 *
 * Paint is on inner Views, never the Pressable (CLAUDE.md, host CSS). The whole toast closes on a
 * press (v1's `closeOnClick`), so the ✕ is a picture, not a second control.
 */
import React, { useSyncExternalStore } from "react";
import { Pressable, Text, View, useWindowDimensions } from "react-native";

import { toasts, type ToastKind } from "@rista/core/toast";
import { Icon } from "@rista/ui/library/components/Icon";
import type { VectorIconName } from "@rista/ui/library/icons/vectors";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { lookFor } from "@rista/ui/library/theme/variants";
import { TOAST_TONE, toastLook } from "./look";

/** react-toastify's compact breakpoint */
const COMPACT_WIDTH = 480;

/** react-toastify's icon per kind, in the icon registry */
const KIND_ICON: Record<ToastKind, VectorIconName> = { info: "toastInfo", success: "toastSuccess", error: "toastError" };

export function ToastHost() {
  const toast = useSyncExternalStore(toasts.subscribe, toasts.current, toasts.current);
  const compact = useWindowDimensions().width <= COMPACT_WIDTH;
  const theme = useTheme();
  if (!toast) return null;
  const look = lookFor(toastLook, theme, { kind: toast.kind, compact });
  // icons are pictures: their fill is the words' role, read off the theme rather than a resolved style
  const ink = theme.color.status[TOAST_TONE[toast.kind]].onSolid;
  return (
    <View pointerEvents="box-none" style={look.container}>
      <Pressable
        key={toast.id}
        testID="toast"
        role="alert"
        accessibilityLiveRegion="polite"
        onPress={toasts.dismiss}
        style={look.press}
      >
        <View style={look.toast}>
          <View style={look.icon}>
            <Icon name={KIND_ICON[toast.kind]} size={22} color={ink} />
          </View>
          <Text style={look.message}>{toast.message}</Text>
          <View style={look.close}>
            <Icon name="toastClose" size={16} color={ink} />
          </View>
        </View>
      </Pressable>
    </View>
  );
}
